/**
 * Ağdan/dışarıdan gelen veri için küçük çalışma zamanı korumaları. Ağdan gelen hiçbir veri
 * doğrulanmadan tipe dönüştürülmez. Zod'un kullanılamadığı yerlerde (widget bütçesi, hafif
 * istemciler) bunlar kullanılır; ağaç sallanabilir ve bağımlılıksızdır.
 */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function hasStringFields<K extends string>(
  value: unknown,
  keys: readonly K[],
): value is Record<K, string> {
  return isRecord(value) && keys.every((key) => typeof value[key] === 'string');
}

/**
 * Dış kaynaktan (JSON, GraphQL, SOAP) gelen bir alanı beklenen tipe indirger. Tip uymuyorsa
 * `undefined` döner; böylece eşleyici "alan yok" ile "alan yanlış tipte"yi aynı yerde ele alır
 * ve tip dönüşümü (`as`) hiç kullanılmaz.
 */
export const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  isRecord(value) ? value : undefined;

/** Boş metin de "yok" sayılır: dış sistemler eksik alanı çoğunlukla `""` ile gönderir. */
export const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? value : undefined;

/** `NaN` ve sonsuz değerler sayı sayılmaz; hesaba girerlerse hatayı sessizce yayarlar. */
export const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/**
 * Ondalıklı tutarı okur; sayı da metin de olabilir.
 *
 * Shopify ve WooCommerce parayı METİN gönderir ("199.90") — kayan noktada kayıp olmasın diye.
 * `asNumber` bunları reddeder ve tutar sessizce düşerdi; sessizce düşen tutar, siparişin hiç
 * okunamaması demektir.
 */
export const asDecimal = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  // `Number('')` sıfırdır; boş alanı sıfır tutar saymak fiyatı yok eder.
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/** Dizi olmayan (ya da eksik) alan boş listeye indirgenir: çağıran döngüyü koşulsuz kurabilir. */
export const asArray = (value: unknown): readonly unknown[] => (Array.isArray(value) ? value : []);

/** JSON metnini güvenle çözer; bozuk girdide `undefined` döner (istisna fırlatmaz). */
export function parseJson(text: string): unknown {
  try {
    const value: unknown = JSON.parse(text);
    return value;
  } catch {
    return undefined;
  }
}
